/**
 * Utilitaires de formatage sécurisés
 */
import { computeVolumeDiscountPercentage } from '@/utils/pricingCore';

/**
 * Affiche une valeur de manière sécurisée (convertit les objets en strings lisibles)
 * Prévient l'erreur React #31 quand on essaie de rendre un objet directement
 * @param value - La valeur à afficher
 * @param fallback - Valeur de fallback si la valeur est undefined/null
 * @returns String sécurisée pour l'affichage
 */
export function safeDisplayValue(value: any, fallback: string = "0"): string {
  if (value === null || value === undefined) {
    return fallback;
  }
  
  if (typeof value === 'string') {
    return value;
  }
  
  if (typeof value === 'number' && !isNaN(value)) {
    return value.toLocaleString('fr-FR');
  }
  
  if (typeof value === 'boolean') {
    return value.toString();
  }
  
  // Pour les objets, on retourne leur représentation JSON ou le fallback
  try {
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  } catch {
    return fallback;
  }
}

/**
 * Formate un nombre en euros de manière sécurisée
 * @param value - La valeur à formater (peut être null, undefined, ou un nombre)
 * @param fallback - Valeur de fallback si la valeur n'est pas un nombre
 * @returns String formatée
 */
export function formatEuro(value: number | null | undefined, fallback: string = "0€"): string {
  if (typeof value === 'number' && !isNaN(value)) {
    return `${safeDisplayValue(value)}€`;
  }
  return fallback;
}

/**
 * Formate un nombre en euros tronqué à l'entier (sans arrondi)
 * Utilisé côté client pour un affichage simplifié
 * @param value - La valeur à formater (peut être null, undefined, ou un nombre)
 * @param fallback - Valeur de fallback si la valeur n'est pas un nombre
 * @returns String formatée sans décimales (ex: "33 647€" au lieu de "33 647,24€")
 */
export function formatEuroTruncated(value: number | null | undefined, fallback: string = "0€"): string {
  if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
    const truncated = Math.trunc(value);
    return `${truncated.toLocaleString('fr-FR')}€`;
  }
  return fallback;
}

/**
 * Formate un nombre de manière sécurisée
 * @param value - La valeur à formater (peut être null, undefined, ou un nombre)
 * @param fallback - Valeur de fallback si la valeur n'est pas un nombre
 * @returns String formatée
 */
export function formatNumber(value: number | null | undefined, fallback: string = "0"): string {
  if (typeof value === 'number' && !isNaN(value)) {
    return safeDisplayValue(value);
  }
  return fallback;
}

/**
 * Convertit les centimes entiers stockés en base vers un affichage en centimes décimaux français
 * @param cents - Centimes entiers (ex: 13)
 * @returns Centimes décimaux formatés (ex: "0,13")
 */
export function formatCentsToDecimal(cents: number | null | undefined): string {
  if (!cents || cents === 0) return '0,00';
  if (typeof cents !== 'number' || isNaN(cents)) return '0,00';
  
  const decimal = cents / 100;
  try {
    return decimal.toLocaleString('fr-FR', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  } catch {
    return '0,00';
  }
}

/**
 * Vérifie si une valeur de prix est valide et non nulle pour l'affichage
 * @param price - La valeur du prix à vérifier
 * @returns true si le prix est valide et supérieur à 0
 */
export function hasValidPrice(price: number | null | undefined): boolean {
  return typeof price === 'number' && !isNaN(price) && price > 0;
}

/**
 * Vérifie si une valeur de durée est valide et non vide pour l'affichage
 * @param duration - La valeur de durée à vérifier
 * @returns true si la durée est valide et non vide
 */
export function hasValidDuration(duration: string | null | undefined): boolean {
  if (!duration || typeof duration !== 'string') return false;
  
  const cleanDuration = duration.trim().toLowerCase();
  // Exclure les valeurs non pertinentes
  const invalidValues = ['null', 'undefined', '0', '', 'null jours', 'undefined jours', '0 jours', '0 jour'];
  
  return !invalidValues.includes(cleanDuration) && cleanDuration.length > 0;
}

/**
 * Vérifie si les données essentielles d'une mission sont présentes pour l'affichage du bloc budget/planning
 * @param mission - L'objet mission à vérifier
 * @returns true si les données sont suffisantes pour afficher le bloc
 */
export function hasValidMissionData(mission: any): boolean {
  return hasValidPrice(mission?.price) || hasValidDuration(mission?.duration);
}

/**
 * Calcule le pourcentage total des remises appliquées en fonction du montant du devis
 * Système de décotes progressives cumulatives :
 * - >50K€ : +2%
 * - >100K€ : +3%
 * - >250K€ : +4%
 * - >350K€ : +6%
 * - >550K€ : +10%
 * @param subtotal - Le montant total du devis (missions + options) HT
 * @param disabledVolumeDiscounts - Paliers désactivés (optionnel, clé = "50k"/"100k"/etc., valeur = true si désactivé)
 * @returns Le pourcentage total de réduction (ex: 25 pour 25%)
 */
export function calculateTotalDiscountPercentage(
  subtotal: number,
  disabledVolumeDiscounts?: { "50k"?: boolean; "100k"?: boolean; "250k"?: boolean; "350k"?: boolean; "550k"?: boolean }
): number {
  return computeVolumeDiscountPercentage(subtotal, disabledVolumeDiscounts);
}

/**
 * Formate l'affichage d'une version de mission
 * @param version - Le numéro de version (0.5, 1.0, 1.5, 2.0, etc.) ou null
 * @returns String formatée pour l'affichage (ex: "Sans version", "MVP (V0.5)", "V1.0")
 */
export function formatVersionDisplay(version: number | null | undefined): string {
  if (version === null || version === undefined) {
    return "Sans version";
  }
  
  if (typeof version !== 'number' || isNaN(version)) {
    return "Sans version";
  }
  
  // Cas spécial pour MVP (0.5)
  if (version === 0.5) {
    return "MVP (V0.5)";
  }
  
  // Format standard pour les autres versions
  return `V${version.toFixed(1)}`;
}